import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { REDESIGN_NS } from "../i18n";
import { useDismissable } from "./useDismissable";

/**
 * Katalog ~2200 yetki taşıyor, hepsini birden basmak listeyi yavaşlatıyor: parça parça
 * gösterilir. Üst sınır DEĞİL, kaydırarak sonuna kadar inilebilir.
 */
const CHUNK = 50;

/** Sonuna bu kadar piksel kala bir sonraki parça eklenir. */
const LOAD_MORE_THRESHOLD = 60;

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  /** Listede olmayan bir değerle Enter'a basıldığında çağrılır. */
  onSubmit?: () => void;
  id?: string;
  ariaLabel?: string;
  /** Katalog henüz gelmediyse liste boş görünmesin, yükleniyor desin. */
  isLoading?: boolean;
}

/**
 * Serbest yazılabilen açılır liste: katalogdan seçilebilir ama listede olmayan
 * bir değer de yazılabilir. Katalog dışı yetkiler ilk kez böyle eklenir.
 */
const Combobox: FC<Props> = ({
  value,
  onChange,
  options,
  placeholder,
  onSubmit,
  id,
  ariaLabel,
  isLoading,
}) => {
  const { t } = useTranslation(REDESIGN_NS);
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = (id || "sso-rd-combobox") + "-list";

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlight(-1);
  }, []);
  useDismissable(anchorRef, isOpen, close);

  const filtered = useMemo(() => {
    const needle = value.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.toLowerCase().indexOf(needle) >= 0);
  }, [options, value]);

  // Filtre değişince baştan başlanır, yoksa dar bir aramada eski sayım kalır.
  useEffect(() => setVisibleCount(CHUNK), [filtered]);

  const matches = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const onListScroll = () => {
    const node = listRef.current;
    if (!node) return;
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (remaining <= LOAD_MORE_THRESHOLD) {
      setVisibleCount((current) =>
        current < filtered.length ? current + CHUNK : current
      );
    }
  };

  // Vurgulanan satır görünür alanda tutulur; liste parça parça büyüdüğü için gerekli.
  useEffect(() => {
    if (!isOpen || highlight < 0) return;
    const node = listRef.current?.children[highlight] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlight]);

  const pick = (option: string) => {
    onChange(option);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlight((current) => {
        const next = current + step;
        if (next < 0) return matches.length - 1;
        if (next >= matches.length) {
          // Klavyeyle de sona takılmamalı: devamı varsa listeyi büyütüp ilk yeni satıra geç.
          if (filtered.length > matches.length) {
            setVisibleCount((count) => count + CHUNK);
            return matches.length;
          }
          return 0;
        }
        return next;
      });
      return;
    }

    if (event.key === "Escape") {
      close();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (isOpen && highlight >= 0 && highlight < matches.length) {
        pick(matches[highlight]);
        return;
      }
      close();
      if (onSubmit) onSubmit();
    }
  };

  return (
    <div className="sso-rd__combobox" ref={anchorRef}>
      <input
        id={id}
        type="text"
        className="sso-rd__input"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={onKeyDown}
      />

      {/* Liste hep DOM'da: koşullu render edilen eleman kapanış animasyonunu
          oynatamaz. Eşleşme yokken `is-open` verilmez, yani boş bir kutu
          görünmez — serbest metin yazarken normal olan durum budur. */}
      <ul
        id={listId}
        ref={listRef}
        role="listbox"
        className={
          "sso-rd__combobox-list" +
          (isOpen && (isLoading || matches.length > 0) ? " is-open" : "")
        }
        onScroll={onListScroll}
      >
        {isLoading ? (
          <li className="sso-rd__combobox-state">{t("Loading")}...</li>
        ) : (
          matches.map((option, index) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                tabIndex={-1}
                className={
                  "sso-rd__combobox-option" +
                  (index === highlight ? " is-active" : "")
                }
                onMouseEnter={() => setHighlight(index)}
                onClick={() => pick(option)}
              >
                {option}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export default Combobox;
